import { useEffect, useState } from "react";
import api, { fmtDate, nzISODate } from "@/lib/api";
import DateInput from "@/components/DateInput";

const SEV = ["Low", "Medium", "High", "Critical"];
const blank = () => ({ description: "", location: "", severity: "Medium", date: nzISODate() });
const errText = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);

function CloseForm({ inc, onDone }) {
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  async function submit(e) {
    e.preventDefault();
    if (note.trim().length < 3) { setErr("Add a short note on what was done."); return; }
    try { await api.post(`/incidents/${inc.id}/close`, { note }); onDone(); }
    catch (e2) { setErr(errText(e2, "Close failed")); }
  }
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-2 items-center mt-1" data-testid={`inc-close-form-${inc.id}`}>
      <input className="input flex-1 min-w-[200px]" placeholder="What was done?" value={note} onChange={e => setNote(e.target.value)} data-testid={`inc-close-note-${inc.id}`} autoFocus />
      <button className="btn-silver text-xs" data-testid={`inc-close-confirm-${inc.id}`}>Close incident</button>
      <button type="button" className="btn-ghost text-xs" onClick={() => onDone(false)} data-testid={`inc-close-cancel-${inc.id}`}>Cancel</button>
      {err && <span className="text-xs text-red-400 w-full" data-testid={`inc-close-error-${inc.id}`}>{err}</span>}
    </form>
  );
}

function IncidentRow({ inc, reload }) {
  const [closing, setClosing] = useState(false);
  const closed = inc.status === "Closed";
  async function reopen() {
    if (!window.confirm("Reopen this incident?")) return;
    try { await api.post(`/incidents/${inc.id}/reopen`); reload(); }
    catch (e) { window.alert(errText(e, "Reopen failed")); }
  }
  return (
    <li className={`border border-neutral-800 rounded px-3 py-2 ${closed ? "opacity-50" : ""}`} data-testid={`inc-row-${inc.id}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <div className="flex-1 min-w-[200px]">
          <div>
            {inc.description}
            {inc.needs_review && !closed && <span className="ml-2 text-[10px] text-amber-300 border border-amber-300 px-1 rounded" data-testid={`inc-review-${inc.id}`}>Reported by {inc.reported_by || "worker"}, needs review</span>}
          </div>
          <div className="text-xs opacity-70">
            {fmtDate(inc.date || inc.created_at)} · {inc.severity || "—"}{inc.location ? ` · ${inc.location}` : ""} · Reported by {inc.reported_by || "—"}
          </div>
          {closed && <div className="text-xs mt-1" data-testid={`inc-closed-info-${inc.id}`}>Closed {fmtDate(inc.closed_at)} by {inc.closed_by}: {inc.close_note}</div>}
        </div>
        <div className="flex items-start gap-2">
          <span className={`text-xs px-2 py-0.5 rounded border ${closed ? "border-neutral-600" : "border-amber-300 text-amber-300"}`} data-testid={`inc-status-${inc.id}`}>{closed ? "Closed" : "Open"}</span>
          {!closed && !closing && <button className="btn-ghost text-xs" onClick={() => setClosing(true)} data-testid={`inc-close-${inc.id}`}>Close</button>}
          {closed && <button className="btn-ghost text-xs" onClick={reopen} data-testid={`inc-reopen-${inc.id}`}>Reopen</button>}
        </div>
      </div>
      {closing && <CloseForm inc={inc} onDone={(ok) => { setClosing(false); if (ok !== false) reload(); }} />}
    </li>
  );
}

export function IncidentsCard({ projectId }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(blank());
  const [err, setErr] = useState("");
  const load = () => api.get(`/projects/${projectId}/hs/incidents`).then(r => setList(r.data));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function add(e) {
    e.preventDefault();
    if (form.description.trim().length < 3) { setErr("Describe the incident."); return; }
    try { await api.post("/incidents", { ...form, project_id: projectId }); setForm(blank()); setErr(""); load(); }
    catch (e2) { setErr(errText(e2, "Could not log incident")); }
  }
  const openCount = list.filter(i => i.status !== "Closed").length;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="card" data-testid="incidents-card">
      <h2 className="text-silver">Incident Register <span className="text-xs opacity-70" data-testid="inc-open-count">({openCount} open)</span></h2>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-6 gap-2 mt-3" data-testid="inc-log-form">
        <input className="input md:col-span-2" placeholder="What happened?" value={form.description} onChange={set("description")} data-testid="inc-description" />
        <input className="input" placeholder="Location" value={form.location} onChange={set("location")} data-testid="inc-location" />
        <select className="input" value={form.severity} onChange={set("severity")} data-testid="inc-severity">{SEV.map(s => <option key={s}>{s}</option>)}</select>
        <DateInput value={form.date} onChange={(v) => setForm({ ...form, date: v })} data-testid="inc-date" />
        <button className="btn-silver" data-testid="btn-log-incident">Log incident</button>
        {err && <div className="text-xs text-red-400 md:col-span-6" data-testid="inc-log-error">{err}</div>}
      </form>
      <ul className="mt-3 space-y-2">
        {list.map(i => <IncidentRow key={i.id} inc={i} reload={load} />)}
        {list.length === 0 && <li className="text-sm opacity-50" data-testid="inc-empty">No incidents recorded.</li>}
      </ul>
    </div>
  );
}
